import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { Button, Grid } from "@mui/material";
import ArrowDropDownSharpIcon from "@mui/icons-material/ArrowDropDownSharp";
import { BarElement, Title, Tooltip, Legend } from "chart.js";
import { Chart as ChartJS, CategoryScale, LinearScale } from "chart.js";

import Label from "../../component/Label";
import { useAppDispatch } from "../../store/hooks";
import { dashboardAction } from "../../store/dashboard/dashboardSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function LeadUpdateTracker() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [selectedMonth, setSelectedMonth] = useState<any>(getCurrentMonth());
  const [leadUpdateTrackerData, setLeadUpdateTrackerData] = useState<any>();
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  function getCurrentMonth() {
    const currentDate = new Date();
    const month = currentDate.toLocaleString("default", { month: "long" });
    return month;
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "top" as const,
      },
    },
    scales: {
      y: {
        border: { dash: [4, 4] }, // for the grid lines
        grid: {
          color: "#aaa", // for the grid lines
          tickColor: "#000", // for the tick mark
          tickBorderDash: [2, 3], // also for the tick, if long enough
          tickLength: 10, // just to see the dotted line
          tickWidth: 2,
          offset: true,
          drawTicks: true, // true is default
          drawOnChartArea: true, // true is default
        },

        beginAtZero: true,
      },
      x: {
        display: true,
        barThickness: 200,
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
        },
        grid: {
          color: "#0095da00", // for the grid lines
          tickColor: "#0095da00", // for the tick mark
          tickLength: 10, // just to see the dotted line
          tickWidth: 2,
          offset: true,
          drawTicks: true, // true is default
          drawOnChartArea: true, // true is default
        },
      },
    },
  };

  const labelsArray = [
    "totalLeads",
    "totalLeadsUpdated24Hours",
    "totalLeadsUpdated",
    "totalDNCLeads",
    "totalActiveLeads",
    "totalWorkshopDoneLeads",
    "totalLeadsCovertedToAdmission",
  ];

  const chartData = {
    labels: [
      ["No. Of", "Leads"],
      ["No. Of Leads", "updated", "within 24hrs"],
      ["No. Of Leads", "Updated"],
      ["DNC", "Leads"],
      ["Active", "Leads"],
      ["No. Of Leads", "Workshop", "done"],
      ["No. Of Leads", "Converted to", "Admission"],
    ],

    datasets: [
      {
        data: labelsArray?.map((item) => leadUpdateTrackerData?.[item]),
        backgroundColor: "#C9DDAF",
        borderRadius: 10,
        paddingRight: 20,
        barThickness: 35,
        maxBarThickness: 40,
      },
    ],
  };

  useEffect(() => {
    getLeadUpdateTrackerData();
  }, [selectedMonth]);

  const getLeadUpdateTrackerData = () => {
    dispatch(
      dashboardAction({
        apiAction: "leadUpdateTracker",
        month: selectedMonth,
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setLeadUpdateTrackerData(result?.payload?.data?.data?.TotalLeads);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  const handleMonthDropdownOpen = () => {
    setMonthDropdownOpen(true);
  };

  const handleMonthSelect = (months: string) => {
    setSelectedMonth(months);
    setMonthDropdownOpen(false);
  };

  const generatePastMonths = () => {
    const currentDate = new Date();
    const pastMonths = [];
    for (let i = 0; i < 5; i++) {
      const month = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      pastMonths.push(month.toLocaleString("default", { month: "long" }));
    }
    return pastMonths;
  };

  const pastMonths = generatePastMonths();

  return (
    <div
      className="padding-y-10 padding-left-10 margin-right-10"
      onClick={() => {
        if (monthDropdownOpen) setMonthDropdownOpen(false);
      }}
    >
      <div className="flex justify-content-between align-item-center">
        <div className=" dashboard-heading padding-y-10 ">
          <div className="font-14 bolder ">{t("leadUpdateTracker")}</div>
        </div>

        <div className=" dashboard-heading padding-y-10 ">
          <Grid className="padding-x-10 ">
            <div>
              <Button
                variant="contained"
                className={
                  monthDropdownOpen === true ? "sms-active-btn" : "sms-btn"
                }
                onClick={handleMonthDropdownOpen}
              >
                {selectedMonth}
                <ArrowDropDownSharpIcon />
              </Button>

              {monthDropdownOpen === true ? (
                <div className="sms-box">
                  {pastMonths.map((month, index) => (
                    <div
                      key={index}
                      className="border-bottom dropdown-month padding-y-10"
                      onClick={() => handleMonthSelect(month)}
                    >
                      <Label title={month} />
                    </div>
                  ))}
                </div>
              ) : (
                ""
              )}
            </div>
          </Grid>
        </div>
      </div>

      <Bar options={options} data={chartData} height={135} />
    </div>
  );
}
